import { computed, inject, Injector, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { CommonForm, CommonNumberField, CommonNumberFieldConfig, CommonOptionsFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { StaticDataService } from "../../../../../../../services";
import { AcceleratorAreaService } from "../../../../accelerator-area.service";
import { AwardEditorDialogDataService } from "./award-editor-dialog-data.service";


export interface AwardFormFields {
	accVentureId: number,
	awardNameId: number,
	decidingRoleId: number,
	awardKindId: number,
	value: number,
	notes: string,
}


export class AwardEditorDialogForm {

	public readonly form = new CommonForm<AwardFormFields>(inject(Injector));

	private readonly dialogDataService = inject(AwardEditorDialogDataService);
	private readonly staticDataService = inject(StaticDataService);
	private readonly accAreaService = inject(AcceleratorAreaService);

	public data = computed(() => this.dialogDataService.dialogData());


	// The form config is a signal, so options must be updated via the config signal.
	// Otherwise resetAll() won't restore the default options
	// as option changes, trigger awardName/decidingRole recomputation, which affects the pending value.
	constructor() {
		toObservable(this.dialogDataService.awardNameIds).subscribe(awardNameIds => {
			const options = this.staticDataService.awardnames.all
				.filter(a => this.data()?.awardId || awardNameIds.includes(a.id)) // Filter award names based on allowed IDs or if we're editing an existing award
				.map(a => ({
					value: a.id,
					text: a.name,
				}));
			this.awardName().config.update(config => ({ ...config, options }));
		});

		toObservable(this.dialogDataService.decidingRoleIds).subscribe(decidingRoleIds => {
			const options = this.staticDataService.decidingroles.all
				.filter(r => this.data()?.awardId || decidingRoleIds.includes(r.id)) // Filter deciding roles based on allowed IDs or if we're editing an existing award
				.map(r => ({
					value: r.id,
					text: r.name,
				}));
			this.decidingRole().config.update(config => ({ ...config, options }));
		});
	}


	private ventureOptions = computed(() => {
		const data = this.accAreaService.data();
		if (!data) return [];

		return data.ventures.filter(v => v.status === 'Graduated').map(v => ({
			value: v.accVentureId,
			text: v.ventureName
		}));
	});
	readonly venture = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Venture',
			isRequired: true,
			options: this.ventureOptions(),
			isEnabled: computed(() => !this.data()?.awardId),
		}),
		formFieldName: 'accVentureId',
		form: this.form,
		actualValue: computed(() => this.data()?.accVentureId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	readonly awardName = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Award',
			options: [],
			isRequired: true,
			isSpanish: false,
			isEnabled: computed(() => !this.data()?.awardId),
		}),
		formFieldName: 'awardNameId',
		form: this.form,
		actualValue: computed(() => this.data()?.awardNameId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	readonly decidingRole = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Deciding Role',
			options: [],
			isRequired: true,
			isSpanish: false,
			isEnabled: computed(() => !this.data()?.awardId),
		}),
		formFieldName: 'decidingRoleId',
		form: this.form,
		actualValue: computed(() => this.data()?.decidingRoleId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	private awardKindOptions() {
		return this.staticDataService.awardKinds.all.map(k => ({
			value: k.id,
			text: k.name,
		}));
	};
	readonly awardKind = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Award Kind',
			options: this.awardKindOptions(),
			isRequired: true,
			isSpanish: false,
			isEnabled: computed(() => !this.data()?.awardId),
		}),
		formFieldName: 'awardKindId',
		form: this.form,
		actualValue: computed(() => this.data()?.awardKindId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly value = computed<CommonNumberField>(() => ({
		config: signal<CommonNumberFieldConfig>({
			label: 'Value',
			isRequired: true,
			type: 'dollar',
			max: 1000000,
		}),
		formFieldName: 'value',
		form: this.form,
		actualValue: computed(() => this.data()?.value),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly notes = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Notes',
			min: 0,
			fixCase: 'sentence-case',
			isRequired: false,
			multiLine: true,
			isSpanish: false
		}),
		actualValue: computed(() => this.data()?.notes ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'notes',
		form: this.form,
	}));
}